import { PERMISSION_AREAS } from '@pidelo/contracts';
import { formatPhone, useLogout } from '@pidelo/web-core';
import { Avatar, Badge, Button, ConfirmDialog, Section, Sheet, Switch } from '@pidelo/web-ui';
import { LogOut } from 'lucide-react';
import { useState } from 'react';
import { session } from '../app/core.ts';
import type { ShiftControls } from './ShiftPanel.tsx';
import styles from './AccountSheet.module.css';

export type AccountSheetProps = { open: boolean; onClose: () => void; shift: ShiftControls | null };

/** Your account: who you are, what you may do, your shift and «Cerrar sesión». */
export function AccountSheet({ open, onClose, shift }: AccountSheetProps) {
  const current = session(state => state.session);
  const logout = useLogout(session);
  const [confirming, setConfirming] = useState(false);
  if (!current) return null;
  const name = `${current.user.firstName} ${current.user.lastName}`.trim();
  return (
    <>
      <Sheet open={open} onClose={onClose} title="Tu cuenta">
        <div className={styles.body}>
          <div className={styles.who}>
            <Avatar name={name} size="lg" />
            <span className={styles.identity}>
              <b>{name}</b>
              <small>{formatPhone(current.user.phone)}</small>
            </span>
          </div>
          {shift ? (
            <Section title="Tu turno" headingLevel={3} description="Mientras estás de turno te llegan los pagos para verificar y los reclamos para responder.">
              <Switch
                checked={shift.shift !== null}
                onChange={on => (on ? shift.start() : shift.end())}
                loading={shift.pending}
                disabled={shift.loading}
                label={shift.shift ? 'Estás de turno' : 'Estás fuera de turno'}
              />
            </Section>
          ) : null}
          <Section title="Lo que puedes hacer" headingLevel={3}>
            {current.isOwner ? (
              <p className={styles.owner}>
                <Badge tone="plum">Principal</Badge> Ves y haces todo en el admin, y das los permisos al equipo.
              </p>
            ) : (
              <ul className={styles.permissions}>
                {PERMISSION_AREAS.map(area => {
                  const chosen = area.actions.filter(action => current.permissions.some(permission => permission === action.id));
                  return chosen.length > 0 ? (
                    <li key={area.id}>
                      <b>{area.title}</b>
                      <small>{chosen.length === area.actions.length ? 'Todo' : chosen.map(action => action.title).join(' · ')}</small>
                    </li>
                  ) : null;
                })}
              </ul>
            )}
          </Section>
          <Button variant="ghost" tone="danger" icon={<LogOut size={18} aria-hidden="true" />} onClick={() => setConfirming(true)} fullWidth>
            Cerrar sesión
          </Button>
        </div>
      </Sheet>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="¿Cerrar la sesión?"
        confirmLabel="Cerrar sesión"
        tone="danger"
        onConfirm={async () => {
          await logout.mutateAsync();
        }}
      >
        Para volver a entrar te mandaremos un código al correo.
      </ConfirmDialog>
    </>
  );
}
